import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const texts = {
  title: "Partenariat",
  heroImage: "https://net-basis.net/Bilder/netbasis-partnership-dach-market-entry.webp",
  heroAlt: "Un homme d'affaires tend la main pour un partenariat de confiance et l'entrée sur le marché DACH.",
  heroClaimLine1: "Forts par le",
  heroClaimLine2: "partenariat !",
  h1Text: "Votre lien de confiance pour un succès commercial international. Nous surmontons les obstacles culturels, linguistiques et logistiques pour implanter les fabricants étrangers de TIC de manière sûre et efficace dans la région DACH.",
  introParagraphs: [
    "Dès l'instant où des mots comme « international » ou « étranger » apparaissent dans vos réflexions et activités commerciales, les processus qui en découlent deviennent généralement plus complexes. D'innombrables facteurs doivent être pris en compte : les barrières linguistiques, les situations juridiques confuses ou encore des décalages horaires importants peuvent survenir et perturber les planifications établies. Enfin, la difficulté d'être personnellement sur place pour suivre ce qui se passe réellement constitue un obstacle majeur pour de nombreux fabricants.",
    "C'est précisément sur ce point que NetBasis International est le partenaire idéal à vos côtés. En tant qu'entreprise de conseil, d'importation et de commerce de longue date et hautement expérimentée, nous agissons comme votre solide relais local. Nous intervenons en tant que médiateur qualifié sur le terrain et vous préservons efficacement des erreurs critiques ou fatales pour votre activité qui peuvent survenir lors de l'entrée sur le marché international.",
    "Nos fabricants partenaires internationaux bénéficient directement de notre connaissance approfondie du marché et de notre présence solide dans l'ensemble de l'espace économique germanophone. En tant qu'importateur opérationnel, nous vous mettons en contact direct avec toutes les sociétés de vente et organisations commerciales locales pertinentes en une seule et unique étape. Avec NetBasis International et les contacts de haut niveau qui nous sont associés, le lancement de produits exceptionnels et de solutions TIC d'avenir devient nettement plus simple et efficace.",
    "Dans le même temps, nos partenaires de distribution locaux apprécient de collaborer avec une entreprise basée en Allemagne comme NetBasis International. Ils bénéficient ainsi d'un accès immédiat et simple à un large portefeuille de produits et de solutions innovants provenant de divers fabricants étrangers. Nous sommes fiers de coopérer déjà avec succès avec un grand nombre de fabricants de premier plan non concurrents.",
  ],
  experienceHeading: "Un riche capital d'expérience",
  experienceParagraphs: [
    "Les marchés changent — le savoir, les contacts et l'expérience demeurent.",
    "Depuis près de deux décennies, NetBasis International accompagne avec succès le développement de fabricants étrangers et de marques technologiques dans la région DACH, en les implantant durablement sur le marché et en assurant un suivi à long terme. Pour nous, il ne s'agit jamais uniquement de vente pure. C'est une question de confiance véritable et de compréhension profonde des deux parties — la marque mondiale qui souhaite prendre pied sur un nouveau marché, et le client local à la recherche d'un partenaire fiable.",
    "Des relations de confiance et durables avec nos partenaires ne sont pas un simple avantage secondaire, elles sont le fondement même d'un succès pérenne et de clients satisfaits.",
    "Les fabricants vont et viennent, mais notre capacité à analyser précisément les marchés et à créer les bonnes connexions reste inchangée. Vous trouverez ci-dessous un aperçu des partenariats importants tissés au cours de ces presque vingt dernières années.",
  ],
  manufacturers: [
    {
      name: "LeadDoubler ApS",
      logo: "https://net-basis.net/Bilder/Lead-Doubler-logo.png",
      desc: "Fabricant danois de logiciels de génération de leads",
    },
    {
      name: "Mondago Ltd",
      logo: "https://net-basis.net/Bilder/Mondago-logo.png",
      desc: "Fabricant anglais de solutions CTI",
    },
    {
      name: "Pinewood",
      logo: "https://net-basis.net/Bilder/Pinewood-logo.jpg",
      desc: "Fabricant belge de solutions multimédias",
    },
    {
      name: "Retell Call Recording",
      logo: "https://net-basis.net/Bilder/Re-Tell-logo.png",
      desc: "Fabricant anglais de solutions d'enregistrement d'appels",
    },
    {
      name: "Rohde & Schwarz Topex",
      logo: "https://net-basis.net/Bilder/Rohde-Schwarz-logo.jpg",
      desc: "Fabricant roumain de passerelles GSM",
    },
    {
      name: "Samsung",
      logo: "https://net-basis.net/Bilder/Samsung-logo.png",
      desc: "Collaboration dans le domaine de leurs solutions CTI",
    },
    {
      name: "Voice gear",
      logo: "https://net-basis.net/Bilder/Voice-Gear-logo.jpg",
      desc: "Marque commerciale internationale de solutions audio",
    },
    {
      name: "Zenion",
      logo: "https://net-basis.net/Bilder/Zenion-logo.jpg",
      desc: "Marque commerciale internationale de mini-PCs fanless",
    },
  ],
  claim: "NetBasis International - drive to excellence with excellent drivers !",
};

const renderWithBold = (text: string) => {
  const parts = text ? text.split(COMPANY_NAME) : [];
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <strong className="text-primary-600">{COMPANY_NAME}</strong>
      )}
    </span>
  ));
};

export default function PartenairesPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Partenariat | NetBasis International"
        description="Partenariats B2B établis et un riche capital d’expérience dans la région DACH. Nous créons les bonnes connexions pour un succès durable."
        ogUrl="https://net-basis.net/fr/partenariat"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={texts.heroImage}
                alt={texts.heroAlt}
                title={texts.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-end justify-center px-6 md:px-10 pointer-events-none">
                <div className="flex flex-col items-end -translate-y-14 md:-translate-y-28">
                  <p
                    className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right animate-[bdTextSlide_12s_ease-in-out_infinite]"
                    style={{
                      fontFamily: '"Open Sans", sans-serif',
                      paddingRight: '0.5rem',
                    }}
                  >
                    <span className="block">{texts.heroClaimLine1}</span>
                    <span className="block">{texts.heroClaimLine2}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* WIDGET 1 - Überschrift (jetzt h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {texts.title}
            </h2>

            {/* WIDGET 2 - H1: fetter Fließtext */}
            <h1 className="text-foreground-950 font-semibold text-sm md:text-base leading-relaxed mb-4">
              {texts.h1Text}
            </h1>

            {/* WIDGET 3 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.introParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={idx}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* WIDGET 4 - Überschrift (h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {texts.experienceHeading}
            </h2>

            {/* WIDGET 5 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.experienceParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={`exp-${idx}`}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* Partner-Logos - UNVERÄNDERT */}
            <div className="mt-8 md:mt-10 space-y-6 md:space-y-8">
              {texts.manufacturers.map((m, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-6 md:gap-10"
                >
                  <div className="overflow-hidden w-36 h-24 sm:w-40 sm:h-28 md:w-48 md:h-32 flex-shrink-0 flex items-center justify-center">
                    <img loading="lazy"
                      src={m.logo}
                      alt=""
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm sm:text-lg md:text-xl font-bold text-primary-600 mb-1">
                      {m.name}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground-950 leading-relaxed">
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {texts.claim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}